import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useReducedMotion, useInView } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { IMG, VIDEO } from './dionData';
import { Reveal, ThinLine } from './DionShared';

/* SCENE 04 — ENGINEERING / PRECISION */
export const EngineeringScene = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const path = useTransform(scrollYProgress, [0.05, 0.45], [0, 1]);
  const bpOpacity = useTransform(scrollYProgress, [0.4, 0.62], [1, 0.12]);
  const imgOpacity = useTransform(scrollYProgress, [0.42, 0.72], [0, 1]);
  const imgRotate = useTransform(scrollYProgress, [0.42, 1], [-7, 0]);
  const imgScale = useTransform(scrollYProgress, [0.42, 1], [1.14, 1]);
  const textOpacity = useTransform(scrollYProgress, [0.15, 0.32], [0, 1]);
  const labels = ['Präzision', 'Geometrie', 'Handhabung'];

  return (
    <section ref={ref} className="relative h-[260vh] bg-[#07090e]" data-testid="dion-engineering">
      <div className="sticky top-0 flex h-[100svh] items-center overflow-hidden">
        <div className="mx-auto grid w-full max-w-[1600px] items-center gap-10 px-5 sm:px-8 lg:grid-cols-2">
          <div className="relative order-2 aspect-[3/2] lg:order-1">
            <motion.svg viewBox="0 0 500 320" className="absolute inset-0 h-full w-full" style={reduced ? { opacity: 0.2 } : { opacity: bpOpacity }} aria-hidden="true">
              <motion.path d="M40 210 L 300 150 L 430 132 L 470 128" fill="none" stroke="#5b8da8" strokeWidth="1"
                style={reduced ? { pathLength: 1 } : { pathLength: path }} />
              <motion.path d="M40 210 L 300 178 L 430 172 L 470 174" fill="none" stroke="#5b8da8" strokeWidth="1"
                style={reduced ? { pathLength: 1 } : { pathLength: path }} />
              <motion.path d="M40 210 L 20 250 M470 128 L 470 174" fill="none" stroke="#5b8da8" strokeWidth="0.6" strokeDasharray="4 4"
                style={reduced ? { pathLength: 1 } : { pathLength: path }} />
              <motion.circle cx="300" cy="164" r="30" fill="none" stroke="#5b8da8" strokeWidth="0.5" strokeDasharray="3 4"
                style={reduced ? { pathLength: 1 } : { pathLength: path }} />
            </motion.svg>
            <motion.div className="absolute inset-0 overflow-hidden" style={reduced ? { opacity: 1 } : { opacity: imgOpacity, rotate: imgRotate, scale: imgScale }}>
              <img src={IMG.instrument} alt="Photorealistische Präzisions-Instrument-Studie — Symbolbild" className="dh-media-img" />
            </motion.div>
          </div>

          <motion.div className="order-1 lg:order-2" style={reduced ? undefined : { opacity: textOpacity }}>
            <span className="dh-eyebrow">04 — Präzision</span>
            <h2 className="dh-display mt-6 text-[clamp(2rem,5vw,3.6rem)] text-white">
              PRÄZISION BEGINNT<br /><span className="text-white/55">VOR DER BEHANDLUNG.</span>
            </h2>
            <ThinLine width="60px" delay={0.2} className="mt-7" />
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70">
              Medizinische Anforderungen, ingenieurtechnisch weitergedacht. Wir verbinden klinische Erfahrung
              mit einem präzisen Blick auf Instrumente und Abläufe.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {labels.map((l) => (
                <span key={l} className="dh-kicker flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-[#5b8da8]" /> {l}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* SCENE 05 — LONG HAIR FUE */
export const LongHairScene = () => {
  const videoRef = useRef(null);
  const inView = useInView(videoRef, { amount: 0.4 });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) { v.play?.().catch(() => {}); } else { v.pause?.(); }
  }, [inView]);

  const points = ['Präzise Graftpräparation.', 'Lange Haarschäfte erhalten.', 'Keine vollständige Rasur erforderlich.'];

  return (
    <section id="long-hair-fue" className="relative overflow-hidden bg-[#0a0d14] py-24 lg:py-36" data-testid="dion-long-hair">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
        <Reveal className="relative aspect-video overflow-hidden bg-[#111827]">
          <video
            ref={videoRef} className="dh-media-img" muted loop playsInline preload="metadata"
            data-testid="long-hair-video" aria-label="Reale Aufnahme der Graftpräparation in der Dion Hair Clinic"
          >
            <source src={VIDEO.longHair} type="video/mp4" />
          </video>
          <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10" />
          <span className="dh-kicker absolute bottom-4 left-4 text-white/70">Reale Klinikaufnahme · Graftpräparation</span>
        </Reveal>

        <div>
          <Reveal>
            <span className="dh-eyebrow">05 — Signature</span>
            <h2 className="dh-display mt-6 text-[clamp(2.2rem,5vw,3.8rem)] text-white">LONG HAIR FUE</h2>
            <p className="mt-4 text-base font-light text-white/75">Haartransplantation ohne vollständige Rasur.</p>
          </Reveal>
          <Reveal delay={0.12} className="mt-8">
            <ThinLine width="60px" />
            <ul className="mt-6 space-y-4">
              {points.map((p) => (
                <li key={p} className="flex items-baseline gap-3 border-t border-white/10 pt-4 text-sm text-white/80">
                  <span className="text-[#5b8da8]">—</span> {p}
                </li>
              ))}
            </ul>
            <Link to="/longhairfue" className="dh-textlink mt-8" data-testid="long-hair-fue-cta">
              Long Hair FUE entdecken <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
